import React from 'react';
import { Box, Typography, Card, Container, Grid, Chip } from '@mui/joy';
import { motion } from 'framer-motion';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

const AboutPage = () => {
  const values = [
    {
      icon: <AutoAwesomeIcon />,
      title: 'AI-First Approach',
      description: 'We strategically integrate AI where it creates real business value, not just for technology\'s sake.'
    },
    {
      icon: <TrendingUpIcon />,
      title: 'Business Impact',
      description: 'Every solution we build is designed to deliver measurable ROI and competitive advantage.'
    },
    {
      icon: <SecurityIcon />,
      title: 'Enterprise Quality',
      description: 'Rock-solid reliability with 99.9% uptime and enterprise-grade security standards.'
    },
    {
      icon: <RocketLaunchIcon />,
      title: 'Future-Proof Solutions',
      description: 'Technology that adapts and learns, growing alongside your business needs.'
    }
  ];

  const expertise = [
    { area: 'AI Strategy & Integration', experience: '5+ Years' },
    { area: 'Full-Stack Development', experience: '8+ Years' },
    { area: 'Cloud Infrastructure', experience: '6+ Years' },
    { area: 'Business Intelligence', experience: '7+ Years' },
    { area: 'Process Automation', experience: '4+ Years' },
    { area: 'Enterprise Solutions', experience: '10+ Years' }
  ];

  return (
    <Box sx={{ py: 12, minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography 
              level="h1" 
              sx={{ 
                mb: 4,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 50%, #00A86B 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              About Calvet Technology Solutions
            </Typography>
            <Typography 
              level="h3" 
              sx={{ 
                color: 'text.primary',
                maxWidth: 800,
                mx: 'auto',
                mb: 4,
                lineHeight: 1.6
              }}
            >
              We Build Solutions That Think Ahead
            </Typography>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'text.secondary',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
                fontWeight: 400
              }}
            >
              Technology consultants who strategically integrate AI to solve real business problems. 
              We don't just build software – we craft intelligent solutions.
            </Typography>
          </Box>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Card
            sx={{
              p: 6,
              mb: 10,
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              textAlign: 'center'
            }}
          >
            <Box sx={{ mb: 4 }}>
              <IntegrationInstructionsIcon sx={{ fontSize: '3rem', color: '#2E5BBA', mb: 2 }} />
              <Typography level="h2" sx={{ color: 'text.primary', mb: 3 }}>
                Our Mission
              </Typography>
              <Typography 
                level="h4" 
                sx={{ 
                  color: 'text.secondary',
                  lineHeight: 1.8,
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                To help businesses harness the transformative power of artificial intelligence through 
                strategic integration that delivers measurable results and competitive advantage.
              </Typography>
            </Box>
          </Card>
        </motion.div>

        {/* Our Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Box sx={{ mb: 10 }}>
            <Typography 
              level="h2" 
              sx={{ 
                textAlign: 'center',
                mb: 6,
                color: 'text.primary'
              }}
            >
              What Drives Us
            </Typography>
            <Grid container spacing={4}>
              {values.map((value, index) => (
                <Grid key={value.title} xs={12} md={6}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <Card
                      sx={{
                        p: 4,
                        height: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            flexShrink: 0,
                            '& svg': { fontSize: '1.8rem' }
                          }}
                        >
                          {value.icon}
                        </Box>
                        <Box>
                          <Typography 
                            level="h4" 
                            sx={{ 
                              color: 'text.primary',
                              mb: 2,
                              fontWeight: 600
                            }}
                          >
                            {value.title}
                          </Typography>
                          <Typography 
                            level="body1" 
                            sx={{ 
                              color: 'text.secondary',
                              lineHeight: 1.6
                            }}
                          >
                            {value.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        {/* Our Expertise */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <Box sx={{ mb: 10 }}>
            <Typography 
              level="h2" 
              sx={{ 
                textAlign: 'center',
                mb: 6,
                color: 'text.primary'
              }}
            >
              Our Expertise
            </Typography>
            <Grid container spacing={3}>
              {expertise.map((item, index) => (
                <Grid key={item.area} xs={12} sm={6} md={4}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                  >
                    <Card
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          border: '1px solid rgba(46, 91, 186, 0.3)',
                        }
                      }}
                    >
                      <Typography 
                        level="h4" 
                        sx={{ 
                          color: 'text.primary',
                          mb: 2,
                          fontWeight: 600
                        }}
                      >
                        {item.area}
                      </Typography>
                      <Chip
                        variant="soft"
                        sx={{
                          background: 'rgba(46, 91, 186, 0.1)',
                          color: '#2E5BBA',
                          border: '1px solid rgba(46, 91, 186, 0.2)',
                          fontWeight: 600
                        }}
                      >
                        {item.experience}
                      </Chip>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        {/* Why Choose Us */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <Card
            sx={{
              p: 6,
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              textAlign: 'center'
            }}
          >
            <Typography 
              level="h2" 
              sx={{ 
                color: 'text.primary',
                mb: 4
              }}
            >
              Why Choose Calvet Technology Solutions?
            </Typography>
            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid xs={12} md={4}>
                <Typography 
                  level="h1" 
                  sx={{ 
                    fontSize: '3rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mb: 1
                  }}
                >
                  40%
                </Typography>
                <Typography level="h4" sx={{ color: 'text.primary', mb: 1 }}>
                  Cost Reduction
                </Typography>
                <Typography level="body2" sx={{ color: 'text.secondary' }}>
                  Through smart automation
                </Typography>
              </Grid>
              <Grid xs={12} md={4}>
                <Typography 
                  level="h1" 
                  sx={{ 
                    fontSize: '3rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mb: 1
                  }}
                >
                  99.9%
                </Typography>
                <Typography level="h4" sx={{ color: 'text.primary', mb: 1 }}>
                  Uptime
                </Typography>
                <Typography level="body2" sx={{ color: 'text.secondary' }}>
                  Rock-solid reliability
                </Typography>
              </Grid>
              <Grid xs={12} md={4}>
                <Typography 
                  level="h1" 
                  sx={{ 
                    fontSize: '3rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #2E5BBA 0%, #00A86B 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mb: 1
                  }}
                >
                  24/7
                </Typography>
                <Typography level="h4" sx={{ color: 'text.primary', mb: 1 }}>
                  Monitoring
                </Typography>
                <Typography level="body2" sx={{ color: 'text.secondary' }}>
                  Proactive issue prevention
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AboutPage;